<template>
	<div>
		<div class="flex items-center justify-between pt-12 px-8 sm:pt-16 sm:pb-4 sm:px-24">
			<div v-if="polyline[Math.max(selectedIndex, 0)].time_from_start" class="flex items-center text-sm">
				<Icon :icon="icons.time" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 60px">{{ polyline[Math.max(selectedIndex, 0)].time_from_start }}</span>
			</div>
			<div class="flex items-center text-sm">
				<Icon :icon="icons.distance" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 70px">{{ polyline[Math.max(selectedIndex, 0)].distance_from_start < 1000 ? `${Math.round(polyline[Math.max(selectedIndex, 0)].distance_from_start)} m` : `${n(polyline[Math.max(selectedIndex, 0)].distance_from_start / 1000, {minimumFractionDigits: 2, maximumFractionDigits: 2})} km` }}</span>
			</div>
			<div v-if="polyline[Math.max(selectedIndex, 0)].ele" class="flex items-center text-sm">
				<Icon :icon="icons.altitude" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 65px">{{ n(polyline[Math.max(selectedIndex, 0)].ele) }} m</span>
			</div>
			<div v-if="props.paceType" class="flex items-center text-sm">
				<Icon :icon="icons.pace" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 85px">{{ convertSpeedToPace(props.paceType, polyline[Math.max(selectedIndex, 0)].speed) }}</span>
			</div>
			<div v-if="polyline[Math.max(selectedIndex, 0)].time" class="none md:flex items-center text-sm">
				<Icon :icon="icons.clock" x-small class="mr-4 sm:mr-8"/>
				<span style="width: 110px">{{ $date(polyline[Math.max(selectedIndex, 0)].time).format('DD.MM.YYYY HH:mm') }}</span>
			</div>
		</div>
		<div class="mx--20 sm:mx-0">
			<chart v-model:hover-index="selectedIndex" type="area"
				   :data="series"
				   :labels="labels"
				   :x-annotations="xAnnotations"
				   :height="props.height"
				   :options="options"/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {ActivityLatLng} from "@spoferan/spoferan-ts-core";
import {convertSpeedToPace, getPaceTypeLabel, PaceType} from "@spoferan/spoferan-ts-core";
import {computed, ref, watch} from "vue";
import {useI18n, useRuntimeConfig} from "#imports";
import {mdiClockOutline, mdiElevationRise, mdiArrowCollapseRight, mdiSpeedometer, mdiTimerOutline} from "@mdi/js";

const {t, n} = useI18n();
const config = useRuntimeConfig();

const props = withDefaults(defineProps<{
	// The polyline index
	modelValue?: number,

	polyline: ActivityLatLng[],

	// The x-axis markers to insert
	markers?: {title: string, x: number, color?: string}[],

	height?: number|string,
	paceType?: PaceType,
}>(), {
	height: 'auto',
	markers: []
});
const emit = defineEmits(['update:modelValue']);

const selectedIndex = ref(props.modelValue);
watch(selectedIndex, (newValue) => {
	emit('update:modelValue', newValue);
});
watch(() => props.modelValue, (newValue) => {
	selectedIndex.value = newValue;
});

const icons = {
	distance: mdiArrowCollapseRight,
	time: mdiTimerOutline,
	clock: mdiClockOutline,
	altitude: mdiElevationRise,
	pace: mdiSpeedometer,
}

const options = {
	dataLabels: {
		enabled: false
	},
	stroke: {
		curve: 'straight',
		width: 3
	},
	fill: {
		type: 'gradient',

		// We simulate a line chart as area chart for the pace as it's prettier than the real line chart
		gradient: {
			opacityFrom: [0.6, 0],
			opacityTo: [1, 0]
		},

	},
	tooltip: {
		// We show a custom "tooltip" above the chart
		custom: function() {
			return null
		}
	},
	xaxis: {
		type: 'numeric',
		position: 'top',
		tickAmount: 5, // Max pretty amount for mobile devices
		axisTicks: {
			show: false
		},
		labels: {
			rotate: 0,
			formatter: function(value) {
				if (value === 0) {
					return t('label.start');
				}
				return `${n((value / 1000), {maximumFractionDigits: 1})} km`;
			}
		},
		tooltip: {
			enabled: false
		}
	},
	yaxis: [
		{
			title: {
				text: `${t('label.altitude')} (m)`,
				offsetX: 2
			},
			// The lowest altitude - 50m
			min: () => Math.max(0, props.polyline.reduce((minAltitude: number, latLng) => Math.min(minAltitude, latLng.ele), Number.MAX_SAFE_INTEGER) - 50),
			forceNiceScale: true,
			tickAmount: 4,
			labels: {
				offsetX: 5,
				align: 'center',
				formatter: (value) => {
					if (!value) {
						return value;
					}

					return n(value);
				}
			}
		},
		{
			title: {
				text: `${t('label.pace')} (${getPaceTypeLabel(props.paceType)})`,
				offsetX: -2
			},
			opposite: true,
			// The lowest pace
			min: () => Math.max(0, props.polyline.reduce((minAltitude: number, latLng) => Math.min(minAltitude, latLng.speed), Number.MAX_SAFE_INTEGER)),
			tickAmount: 4,
			labels: {
				offsetX: 8,
				align: 'center',
				formatter: (value) => {
					return convertSpeedToPace(props.paceType, value, false);
				}
			}
		},
	],
	legend: {
		floating: true,
		onItemClick: {
			toggleDataSeries: false
		},
	}
};

const labels = computed(() => {
	return props.polyline.map((latLng: ActivityLatLng) => latLng.distance_from_start);
});

// The annotations are only inserted at the correct x position for desktop devices
const xAnnotations = computed(() => {
	return props.markers.map((marker) => {
		return {
			x: marker.x,
			borderColor: marker.color ?? config.public.colors.primary,
			label: {
				show: true,
				text: marker.title,
				borderColor: "#fff",
				style: {
					color: "#fff",
					background: marker.color ?? config.public.colors.primary
				}
			}
		}
	});
});

const series = computed(() => {
	return [
		{
			name: t('label.altitude'),
			data: props.polyline.map((latLng: ActivityLatLng) => {
				return [latLng.distance_from_start, latLng.ele.toFixed(1)];
			}),
		},
		{
			name: t('label.pace'),
			data: props.polyline.map((latLng: ActivityLatLng) => {
				return [latLng.distance_from_start, latLng.speed];
			}),
		}
	];
});
</script>
<style >
.apexcharts-tooltip {
  display: none;
}
</style>